import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './DoCityOnboardingTour.css';
import PropertiesService from '../../Services/PropertiesService';
import requestsService from '../../Services/requests';
import { getServicesIntroDismissedKey } from '../../utils/onboardingStorage';

const STEPS = [
  {
    targetSelector: '[data-onboarding="dcp-card"]',
    triggerSelector: '[data-onboarding="dcp-button"]',
    content: 'DCP is DoCity Customer Property. Choose Continue to begin a property review with the details and documents needed for an engineer-led assessment.',
    spotlightPadding: 16
  },
  {
    targetSelector: '[data-onboarding="form-screen"]',
    triggerSelector: 'form[data-onboarding-form]',
    content: 'Enter the details for your selected service. This guide will close when you focus a form field.',
    spotlightPadding: 14,
    blurOutsideTarget: true
  },
  {
    targetSelector: '[data-onboarding="format-choice"]',
    spotlightSelector: '[data-onboarding="form-screen"]',
    triggerSelector: '.prestige-form',
    content: 'Step 2: choose the document format that matches your records. The upload fields will appear below it.',
    spotlightPadding: 14,
    blurOutsideTarget: true
  },
  {
    targetSelector: '[data-onboarding="document-upload"]',
    spotlightSelector: '[data-onboarding="form-screen"]',
    triggerSelector: '[data-onboarding="document-upload"]',
    content: 'Upload the requested property documents here. Follow the document categories and use Submit to continue to review.',
    spotlightPadding: 14,
    blurOutsideTarget: true
  },
  {
    targetSelector: '[data-onboarding="review-step"]',
    spotlightSelector: '[data-onboarding="form-screen"]',
    triggerSelector: '[data-onboarding="review-step"]',
    content: 'Step 3: review your property details and uploaded documents. Submit when everything is correct to continue to verification and payment.',
    spotlightPadding: 14,
    blurOutsideTarget: true
  },
  {
    targetSelector: '[data-onboarding="aadhaar-verification"]',
    triggerSelector: '[data-onboarding="aadhaar-verification"]',
    content: 'Step 4: enter your 12-digit Aadhaar number and verify it. Then continue to payment to complete your Request Property application.',
    spotlightPadding: 12,
    blurOutsideTarget: true
  },
  {
    targetSelector: '[data-onboarding="first-question"]',
    triggerSelector: '[data-onboarding="first-question"]',
    content: 'Answer the first DCP question by choosing Yes or No. After you answer, the onboarding guide will close and the rest of the process will continue as usual.',
    spotlightPadding: 12,
    blurOutsideTarget: true,
    completeOnRadioChoice: true
  }
];

const DoCityOnboardingTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastScrolledStep, setLastScrolledStep] = useState(-1);
  const [waitingForNextStep, setWaitingForNextStep] = useState(false);
  const [dismissedScreen, setDismissedScreen] = useState('');
  const checkedUsersRef = useRef(new Set());

  // Keep the tour layer out of the way of payment and other application
  // dialogs. Its full-screen blur panels otherwise sit above modal content.
  useEffect(() => {
    const updateModalState = () => {
      const modalIsOpen = Boolean(document.querySelector(
        '.modal.show, .modal-backdrop.show, [data-payment-modal="true"], .onboarding-demo-backdrop'
      ));
      setIsModalOpen((current) => current === modalIsOpen ? current : modalIsOpen);
      if (modalIsOpen) setTargetRect(null);
    };

    updateModalState();
    const observer = new MutationObserver(updateModalState);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'aria-hidden']
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const nextStep = path === '/requestProperty' || path === '/kyp' || path === '/soc'
      ? 1
      : path === '/reqProperty' ? STEPS.length - 1 : 0;
    setCurrentStep(nextStep);
    setWaitingForNextStep(false);
    setDismissedScreen('');
    setTargetRect(null);
    setLastScrolledStep(-1);
  }, [location.pathname]);

  useEffect(() => {
    let apiCheckDone = false;

    const checkAuthAndHistory = async () => {
      const token = localStorage.getItem("token");
      setIsLoggedIn(!!token);
      
      if (token) {
        if (localStorage.getItem('docity_onboarding_active') === 'true') {
          setCompleted(false);
          if (location.pathname === '/reqProperty') {
            setCurrentStep(STEPS.length - 1);
          } else if (['/requestProperty', '/kyp', '/soc'].includes(location.pathname) && currentStep === 0) {
            setCurrentStep(1);
            setWaitingForNextStep(false);
          }
          return;
        }

        if (!apiCheckDone) {
          apiCheckDone = true;
          
          try {
            const userDetails = JSON.parse(localStorage.getItem("userDetails"));
            if (userDetails && userDetails.user_id) {
              const userId = userDetails.user_id;
              if (checkedUsersRef.current.has(String(userId))) return;
              const [dcpRes, kypRes, socRes] = await Promise.all([
                PropertiesService.getUserProperties({ userId }).catch(e => null),
                PropertiesService.getKpiLists({ userId }).catch(e => null),
                requestsService.getsocs({ userId }).catch(e => null)
              ]);

              const hasRequestError = (response) =>
                response == null || (typeof response === 'string' && /^(error:|network error|no authentication)/i.test(response));
              if ([dcpRes, kypRes, socRes].some(hasRequestError)) {
                apiCheckDone = false;
                return;
              }
              checkedUsersRef.current.add(String(userId));

              const getRows = (response) => {
                let value = response;
                for (let depth = 0; depth < 3 && value && !Array.isArray(value); depth += 1) {
                  value = value.data || value.properties || value.items || value.results;
                }
                return Array.isArray(value) ? value : [];
              };
              const dcpData = getRows(dcpRes);
              const kypData = getRows(kypRes);
              const socData = getRows(socRes);

              const hasDcp = Array.isArray(dcpData) && dcpData.length > 0;
              const hasKyp = Array.isArray(kypData) && kypData.length > 0;
              const hasSoc = Array.isArray(socData) && socData.length > 0;

              console.log("Onboarding Tour History Check:", { hasDcp, hasKyp, hasSoc });

              if (hasDcp || hasKyp || hasSoc) {
                localStorage.setItem("docity_onboarding_completed", "true");
                setCompleted(true);
              } else {
                localStorage.setItem('docity_onboarding_active', 'true');
                if (location.pathname === '/requestProperty') {
                  setCurrentStep(1);
                  setWaitingForNextStep(false);
                } else if (location.pathname !== '/') {
                  navigate('/', { replace: true });
                  setCurrentStep(0);
                } else {
                  setCurrentStep(0);
                }
                setCompleted(false);
              }
            } else {
              // If userDetails are missing but they are logged in, default to false to be safe
              setCompleted(false);
            }
          } catch (error) {
            console.error("Failed to check user history for onboarding:", error);
            apiCheckDone = false;
            // Default to starting the tour if the API fails
            setCompleted(false);
          }
        }
      } else {
        apiCheckDone = false;
      }
    };

    checkAuthAndHistory();
    const interval = setInterval(checkAuthAndHistory, 1000);
    return () => clearInterval(interval);
  }, [location.pathname, navigate, currentStep]);

  const measureTarget = useCallback(() => {
    if (completed || !isLoggedIn || isModalOpen) return;

    const step = STEPS[currentStep];
    const el = document.querySelector(step.spotlightSelector || step.targetSelector);

    if (el) {
      if (lastScrolledStep !== currentStep && currentStep !== 0) {
        el.scrollIntoView({ behavior: 'smooth', block: currentStep === 1 ? 'start' : 'center', inline: 'nearest' });
        setLastScrolledStep(currentStep);
      }
      const rect = el.getBoundingClientRect();

      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        padding: step.spotlightPadding
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, completed, isLoggedIn, isModalOpen, lastScrolledStep]);

  useEffect(() => {
    if (!isLoggedIn || completed || isModalOpen) return;

    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, { passive: true });

    const intervalId = setInterval(measureTarget, 500);

    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget);
      clearInterval(intervalId);
    };
  }, [measureTarget, isLoggedIn, completed, isModalOpen]);

  const handleNext = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSkip();
    }
  }, [currentStep]);

  const handleSkip = () => {
    // Skip only the current screen. A route or form-step change gets a fresh guide.
    setWaitingForNextStep(false);
    setDismissedScreen(`${location.pathname}:${currentStep}`);
    setTargetRect(null);
  };

  const completeOnboarding = () => {
    localStorage.removeItem('docity_onboarding_active');
    localStorage.setItem('docity_onboarding_completed', 'true');
    setCompleted(true);
    setTargetRect(null);
  };

  useEffect(() => {
    if (completed || !isLoggedIn || isModalOpen) return;

    const step = STEPS[currentStep];

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      }
    };

    const handleUserAction = (e) => {
      if (waitingForNextStep) return;

      const formField = e.target.closest('input, select, textarea, button, label');
      const insideOnboardedForm = e.target.closest('[data-onboarding="form-screen"]');
      if (step.blurOutsideTarget && formField && insideOnboardedForm) {
        if (step.targetSelector === '[data-onboarding="aadhaar-verification"]') {
          const aadhaarInput = e.target.closest('[data-onboarding="aadhaar-input"]');
          const digits = aadhaarInput?.value?.replace(/\D/g, '') || '';
          if (aadhaarInput && e.type === 'input' && digits.length === 12) {
            setDismissedScreen(`${location.pathname}:${currentStep}`);
            setTargetRect(null);
          }
          return;
        }
        setDismissedScreen(`${location.pathname}:${currentStep}`);
        setTargetRect(null);
        setWaitingForNextStep(false);
        return;
      }

      const triggerEl = step.triggerSelector ? e.target.closest(step.triggerSelector) : e.target.closest(step.targetSelector);

      // For form interactions, make sure we only trigger on actual inputs/buttons, not just clicking the background of the form
      if (triggerEl) {
        // The route change from a service choice selects the matching form
        // guide; don't advance against the old page while navigation is pending.
        if (currentStep === 0) return;
        const screenKey = `${location.pathname}:${currentStep}`;
        if (dismissedScreen === screenKey) return;
        if (step.completeOnRadioChoice) {
          if (e.target.closest('input[type="radio"]')) completeOnboarding();
          return;
        }
        // Once the user starts working in a form, remove its spotlight and
        // blur for this screen. A later form screen gets its own guide.
        if (currentStep > 0 && e.target.closest('input, select, textarea, button, label')) {
          setDismissedScreen(screenKey);
          setTargetRect(null);
          setWaitingForNextStep(false);
          return;
        } else if (step.triggerSelector === 'form[data-onboarding-form]') {
          return;
        }
        setWaitingForNextStep(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Listen to all interaction types globally
    document.addEventListener('click', handleUserAction, true);
    document.addEventListener('input', handleUserAction, true);
    document.addEventListener('change', handleUserAction, true);
    document.addEventListener('focusin', handleUserAction, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleUserAction, true);
      document.removeEventListener('input', handleUserAction, true);
      document.removeEventListener('change', handleUserAction, true);
      document.removeEventListener('focusin', handleUserAction, true);
    };
  }, [completed, isLoggedIn, isModalOpen, currentStep, waitingForNextStep, dismissedScreen, location.pathname]);

  // Handle waiting for the next step's DOM element to appear
  useEffect(() => {
    if (!waitingForNextStep || completed || !isLoggedIn) return;

    if (currentStep >= STEPS.length - 1) {
      handleSkip();
      return;
    }

    const checkNextElement = () => {
      // 1. Check if the exact next step is available
      const nextStep = STEPS[currentStep + 1];
      const nextTarget = document.querySelector(nextStep.targetSelector);
      if (nextTarget && nextTarget.getClientRects().length > 0) {
        setWaitingForNextStep(false);
        setCurrentStep(prev => prev + 1);
        return true;
      }

      return false;
    };

    if (checkNextElement()) return;

    const pollInterval = setInterval(checkNextElement, 150);

    return () => {
      clearInterval(pollInterval);
    };
  }, [waitingForNextStep, currentStep, completed, isLoggedIn]);

  // Advance a dismissed DCP screen when the next step's target becomes visible.
  // The outer form container stays mounted while its internal step changes, so
  // waiting for that container to disappear would leave later steps un-guided.
  useEffect(() => {
    if (location.pathname !== '/requestProperty' || !dismissedScreen || currentStep <= 0 || currentStep >= STEPS.length - 1) return;

    const nextTarget = STEPS[currentStep + 1].targetSelector;
    const advanceWhenNextStepAppears = () => {
      const nextElement = document.querySelector(nextTarget);
      if (nextElement && nextElement.getClientRects().length > 0) {
        setDismissedScreen('');
        setTargetRect(null);
        setCurrentStep((step) => step + 1);
      }
    };

    const intervalId = setInterval(advanceWhenNextStepAppears, 200);
    advanceWhenNextStepAppears();
    return () => clearInterval(intervalId);
  }, [location.pathname, dismissedScreen, currentStep]);

  // Failsafe mechanism: if current element disappears for a long time, skip it
  useEffect(() => {
    if (completed || !isLoggedIn || waitingForNextStep) return;

    const failsafeTimer = setTimeout(() => {
      const step = STEPS[currentStep];
      if (!document.querySelector(step.targetSelector) && currentStep === 0) handleSkip();
    }, 8000);

    return () => clearTimeout(failsafeTimer);
  }, [currentStep, completed, isLoggedIn, waitingForNextStep, handleNext]);

  if (
    !isLoggedIn ||
    completed ||
    isModalOpen ||
    (location.pathname === '/' && currentStep === 0 && localStorage.getItem(getServicesIntroDismissedKey()) !== 'true')
  ) return null;

  const stepInfo = STEPS[currentStep];
  const screenKey = `${location.pathname}:${currentStep}`;
  const isCurrentScreenDismissed = dismissedScreen === screenKey;
  const serviceGuideText = location.pathname === '/kyp'
    ? 'Enter your property details to start the Know Your Property service.'
    : location.pathname === '/soc'
      ? 'Complete the owner and property details to prepare your Self Occupation Certificate.'
      : 'Enter the property details to start your DoCity Customer Property review.';
  const isFormSpotlight = currentStep === 1 || Boolean(stepInfo.spotlightSelector);
  const tooltipTop = isFormSpotlight ? 12 : targetRect?.top + targetRect?.height + 20;
  const tooltipLeft = isFormSpotlight
    ? Math.max(12, Math.min(window.innerWidth - 332, targetRect?.left + targetRect?.width - 320))
    : Math.max(10, Math.min(targetRect?.left || 10, window.innerWidth - 340));

  return (
    <div className="onboarding-tour-container">
      {targetRect && !isCurrentScreenDismissed && stepInfo.blurOutsideTarget && (
        <>
          <div className="onboarding-blur-panel" style={{ top: 0, left: 0, width: '100vw', height: Math.max(0, targetRect.top) }} />
          <div className="onboarding-blur-panel" style={{ top: targetRect.top + targetRect.height, left: 0, width: '100vw', height: Math.max(0, window.innerHeight - targetRect.top - targetRect.height) }} />
          <div className="onboarding-blur-panel" style={{ top: targetRect.top, left: 0, width: Math.max(0, targetRect.left), height: targetRect.height }} />
          <div className="onboarding-blur-panel" style={{ top: targetRect.top, left: targetRect.left + targetRect.width, width: Math.max(0, window.innerWidth - targetRect.left - targetRect.width), height: targetRect.height }} />
        </>
      )}
      {/* Glow outline around the target */}
      {targetRect && !isCurrentScreenDismissed && (
        <div
          className="spotlight-glow"
          style={{
            top: targetRect.top - targetRect.padding,
            left: targetRect.left - targetRect.padding,
            width: targetRect.width + targetRect.padding * 2,
            height: targetRect.height + targetRect.padding * 2,
          }}
        />
      )}

      {/* Dynamic Tooltip beside the target */}
      {targetRect && !waitingForNextStep && dismissedScreen !== screenKey && (
        <div
          className={`onboarding-tooltip ${isFormSpotlight ? 'onboarding-tooltip-form' : targetRect.top + targetRect.height + 20 > window.innerHeight - 150 ? 'tooltip-top' : 'tooltip-bottom'}`}
          style={{
            top: isFormSpotlight ? tooltipTop : targetRect.top + targetRect.height + 20 > window.innerHeight - 150
              ? Math.max(10, targetRect.top - 180)
              : tooltipTop,
            left: tooltipLeft
          }}
        >
          <div className="tooltip-arrow"></div>
          <div className="onboarding-modal-content" style={{ alignItems: 'flex-start', textAlign: 'left', gap: '10px' }}>
            <h3 className="onboarding-text" style={{ fontSize: '1.1rem' }}>{currentStep === 1 ? serviceGuideText : stepInfo.content}</h3>
          </div>

          <div className="onboarding-modal-footer" style={{ marginTop: '20px' }}>
            <div className="step-dots">
              {STEPS.map((_, i) => (
                <span key={i} className={`step-dot ${i === currentStep ? 'active' : ''}`} />
              ))}
            </div>

            <div className="onboarding-controls">
              <button className="onboarding-skip-btn" style={{ padding: '4px 8px', fontSize: '0.9rem' }} onClick={handleSkip}>Skip</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoCityOnboardingTour;
